import Vue from 'vue'
import Vuex from 'vuex'
import { VuexPersistence } from 'vuex-persist'
import { MongoMixins } from '../mixins/mongo-mixins'
import TrafficType from '../assets/Enums/TrafficType'
import AccountType from '../assets/Enums/AccountType'
import AdvertisementType from '../assets/Enums/AdvertisementType'

Vue.use(Vuex)

const vuexPersist = new VuexPersistence({
    key: 'datenight-business-portal',
    storage: window.localStorage,
    reducer: state => ({
        user: state.user,
        token: state.token,
        homePage: state.homePage,
        partnerData: state.partnerData,
        cookieString: state.cookieString,
        businessEvents: state.businessEvents,
        tempGroupDeals: state.tempGroupDeals,
        advertisements: state.advertisements,
        partnerSettings: state.partnerSettings,
        clients: state.clients,
        tempAdvertisements: state.tempAdvertisements,
        lastCookieExpiry: state.lastCookieExpiry,
    }),
})

export default new Vuex.Store({
    plugins: [vuexPersist.plugin],
    state: {
        token: '',
        snackbar: false,
        snackbarText: '',
        paymentDialog: false,
        sessionCharges: [],
        cookieString: null,
        user: null,
        partnerData: null,
        partnerSettings: null,
        clients: [],
        homePage: null,
        advertisements: [],
        tempAdvertisements: [],
        tempGroupDeals: [],
        businessEvents: [],
        lastCookieExpiry: null,
    },
    mixins: [MongoMixins],
    getters: {
        partnerId: state => state.partnerData?._id,
        partnerName: state => state.partnerData?.partnerName,
        partnerEmail: state => state.user?.partnerEmail,
        geoHashGroup: state => state.partnerData?.geoHashGroup,
        isAuthenticated: state => state.user != null,
        isEstablishment: state =>
            state.partnerData?.acctType === AccountType.Bar ||
            state.partnerData?.acctType === AccountType.Restaurant,
        isPlanner: state =>
            state.partnerData?.acctType === AccountType.Promoter ||
            state.partnerData?.acctType === AccountType.EventCoordinator,
        isContractor: state => 
            state.partnerData.acctType === AccountType.Contractor,
        partnerType: state => state.user?.partnerType,
        acctType: state => state.partnerData?.acctType || null,
        sliderAds: state => state.tempAdvertisements?.filter(s => s && s.type === AdvertisementType.SLIDER) || [],
        cardAds: state => state.tempAdvertisements?.filter(s => s && s.type === AdvertisementType.CARD) || [],
        allAds: state => [...(state.tempAdvertisements || []), ...(state.advertisements || [])],
        activeCardAd: state =>
            state.tempAdvertisements.filter(s => s && s.type === AdvertisementType.CARD && s.isActive),
        mapTraffic: state => state.partnerData?.traffic?.views.filter(s => s.trafficType === TrafficType.Map),
        refreshTime: state => (state.lastCookieExpiry ? new Date(state.lastCookieExpiry) : null),
        businessTraffic: state => state.partnerData?.traffic,
        userHours: state => state.partnerData?.hours?.length ? 
            state.partnerData.hours : state.partnerData?.availability || []
    },
    actions: {
        ADD_CHARGE({ commit }, charge) {
            commit('ADD_CHARGE', charge)
        },
        VERIFY_TOKEN({ commit }) {
            const token = this.state.token;
            if (!token) return commit('AUTH_LOGGED_OUT');

            return MongoMixins.methods.verifyToken(token).then((data) => {
                if (!data.VerifyToken?.isValid) {
                    commit('AUTH_LOGGED_OUT');
                }
            }).catch(() => {
                commit('AUTH_LOGGED_OUT');
            })
        },
        TOUCH_API({ commit }) {
            MongoMixins.methods
                .touchApi()
                .then(data => {
                    console.log('touched')
                    commit('UPDATE_COOKIE_EXPIRY', data.data.maxAge)
                })
                .catch(e => {
                    commit('AUTH_LOGOUT')
                    console.log('session touch failed: ' + e.message)
                })
        },
        DELETE_CHARGE({ commit }, index) {
            commit('DELETE_CHARGE', index)
        },
        CLEAR_CHARGES({ commit }) {
            commit('CLEAR_CHARGES')
        },
        MAKE_PAYMENT({ commit }, paymentInfo) {
            commit('MAKE_PAYMENT', paymentInfo)
        },
        UPDATE_SETTINGS({ commit }, settings) {
            commit('UPDATE_SETTINGS', settings);
        },
        UPDATE_MENU({ commit }, URL) {
            commit('UPDATE_MENU_LINK', URL)
        },
        UPDATE_BUSINESS_HOURS({ commit }, hours) {
            commit('UPDATE_BUSINESS_HOURS', hours)
        },
        UPDATE_DAILY_DEALS({ commit }, deals) {
            commit('UPDATE_PARTNER_DEALS', deals)
        },
        SHOW_SNACKBAR({ commit }, message) {
            commit('REVEAL_SNACKBAR', message)
        },
        ADD_GROUP_DEAL({ commit }, deal) {
            commit('ADD_GROUP_DEAL', deal)
        },
        DELETE_GROUP_DEAL({ commit }, deal) {
            commit('DELETE_GROUP_DEAL', deal)
        },
        UPDATE_GROUP_DEAL({ commit }, deal) {
            commit('UPDATE_GROUP_DEAL', deal)
        },
        UPDATE_ADVERTISEMENTS({ commit }) {
            commit('UPDATE_ADVERTISEMENTS')
        },
        UPDATE_ACTIVE_CARD({ commit }, cardAd) {
            commit('UPDATE_ACTIVE_CARD', cardAd)
        },
        CREATE_AD_DESIGN({ commit }, newAd) {
            commit('CREATE_AD_DESIGN', newAd)
            return this.state.tempAdvertisements.length - 1
        },
        DELETE_AD_DESIGN({ commit }, adIndex) {
            commit('DELETE_AD_DESIGN', adIndex)
        },
        UPDATE_AD_DESIGN({ commit }, updateInfo) {
            commit('UPDATE_AD_DESIGN', updateInfo)
        },
        SUBMIT_AD_DESIGN({ commit }, updateInfo) {
            commit('UPDATE_AD_DESIGN', updateInfo)
            return commit('UPDATE_ADVERTISEMENTS')
        },
        ACTIVATE_AD({ commit }, adIndex) {
            commit('ACTIVATE_AD', adIndex)
        },
        SAVE_GROUP_DEALS({ commit }) {
            commit('SAVE_GROUP_DEALS')
        },
        QUICK_PAY({ commit }, charge) {
            commit('QUICK_PAY', charge)
        },
        SHOW_PAYMENT_DIALOG({ commit }) {
            commit('SHOW_PAYMENT_DIALOG')
        },
        HIDE_PAYMENT_DIALOG({ commit }) {
            commit('HIDE_PAYMENT_DIALOG')
        },
        SUBMIT_PAYMENT({ commit }, details) {
            return commit('SUBMIT_PAYMENT', details)
        },
        DELETE_NAV_ITEM({ commit }, i) {
            commit('DELETE_NAV_ITEM', i);
        },
        ADD_NAV_ITEM({ commit }, item) {
            commit('ADD_NAV_ITEM', item);
        },
        UPDATE_THEME_ITEM({ commit }, item) {
            commit('UPDATE_THEME_ITEM', item);
        },
        SUBMIT_SETTINGS({ commit }) {
            commit('SUBMIT_SETTINGS');
        },
        UPDATE_PAGE_DATA({ commit }, data) {
            commit('UPDATE_PAGE_DATA', data);
        },
        UPDATE_PARTNER_DATA({ commit }, data) {
            commit('UPDATE_PARTNER_DATA', data);
        },
        SUBMIT_PARTNER_DATA({ commit }) {
            commit('SUBMIT_PARTNER_DATA');
        },
        SET_SITE_BUILDER_STATE({ commit }, state) {
            commit('SET_SITE_BUILDER', state);
        },
        SET_PARTNER_TYPE({ commit }, type) {
            commit('SET_PARTNER_TYPE', type);
        },
        SUBMIT_CLIENT_LIST({ commit }, file) {
            commit('SUBMIT_CLIENT_LIST', file);
        },
        AUTH_REQUEST({ commit }, user) {
            return new Promise((resolve, reject) => {
                MongoMixins.methods
                    .emailSignIn(user.email, user.password)
                    .then(data => {
                        const cookie = data.headers["Cookie"] || data.headers["Set-Cookie"] || data.headers["set-cookie"];

                        const partnerAppData = data.data.partnerData;
                        const events = data.data.events;
                        console.log('PARTNER_APP_DATA: ' + JSON.stringify(data.data))
                        commit('AUTH_SUCCESS', {
                            partnerData: partnerAppData,
                            trafficData: partnerAppData.traffic,
                            partnerAds: data.data.advertisements || [],
                            settings: data.data.settings,
                            businessEvents: events,
                            homePage: data.data.homePage,
                            maxAge: partnerAppData.maxAge,
                            cookieString: cookie,
                            token: data.data.token
                        })

                        resolve()
                        return false
                    })
                    .catch(error => {
                        console.log('auth error: ' + error)
                        reject(error)
                    })
            })
        },
        AUTH_LOGOUT({ commit }) {
            return new Promise((resolve, reject) => {
                commit('AUTH_LOGOUT')
                MongoMixins.methods
                    .signUserOut()
                    .then(() => {
                        commit('AUTH_LOGGED_OUT')
                        resolve()
                    })
                    .catch(e => {
                        commit('AUTH_LOGGED_OUT')
                        console.log(e)
                        reject(e)
                    })
            })
        },
    },
    mutations: {
        ADD_CHARGE(state, charge) {
            state.sessionCharges.push(charge)
        },
        DELETE_CHARGE(state, index) {
            state.sessionCharges.splice(index, 1)
        },
        CLEAR_CHARGES(state) {
            state.sessionCharges = []
        },
        MAKE_PAYMENT(state, paymentInfo) {
            paymentInfo.charges = state.sessionCharges

            MongoMixins.methods.submitPayment(paymentInfo).then(() => {
                state.sessionCharges = []
            })
        },
        async SUBMIT_CLIENT_LIST(state, file) {
            const result = await MongoMixins.methods.submitClientList(file);

            state.clients = result.data.clients;
        },
        UPDATE_MENU_LINK(state, URL) {
            console.log('local update ' + URL);
            state.partnerData.menuLink = URL;

            if (!URL) {
                console.log(state.homePage);
                const menuItem = state.partnerSettings.navItems.findIndex(n => n.widget == 'MENU');
                state.partnerSettings.navItems.splice(menuItem, 1);
            }
        },
        async VERIFY_TOKEN(state) {
            const verify = await MongoMixins.methods.verifyToken(state.token);

            if (!verify.VerifyToken?.isValid) {
                console.log('token not valid');
                state.token = '';
                state.partnerData = null
                state.homePage = null
                state.partnerSettings = null;
                state.advertisements = []
                state.tempAdvertisements = []
                state.tempGroupDeals = []
                state.user = null
            }
        },
        UPDATE_PARTNER_DEALS(state, deals) {
            state.partnerData.dailyDeals = deals
        },
        UPDATE_COOKIE_EXPIRY(state, maxAge) {
            state.lastCookieExpiry = new Date(new Date().getTime() + maxAge)
        },
        SET_SITE_BUILDER(state, able) {
            console.log('site builder setting ' + able);
            state.partnerSettings.siteBuilder = able;
        },
        SET_PARTNER_TYPE(state, type) {
            state.partnerData.partnerType = type
        },
        DELETE_NAV_ITEM(state, i) {
            state.partnerSettings.navItems.splice(i, 1)
        },
        UPDATE_THEME(state, theme) {
            state.homePage.theme = theme;
        },
        UPDATE_THEME_ITEM(state, item) {
            const i = state.homePage.theme.findIndex(t => t._id == item._id);
            if (i > -1) state.homePage.theme[i] = item;
        },
        async UPDATE_PAGE_DATA(state, data) {
            const page = await MongoMixins.methods.updateBusinessPage(data);

            console.log('update data: ', data);
            state.homePage = page.data.data;
        },
        ADD_NAV_ITEM(state, item) {
            state.partnerSettings.navItems.push(item)
        },
        AUTH_SUCCESS(state, appData) {
            state.lastCookieExpiry = new Date(new Date().getTime() + appData.maxAge)
            state.user = {
                partnerName: appData.partnerData.partnerName,
                partnerEmail: appData.partnerData.partnerEmail,
                acctType: appData.partnerData.acctType,
                phoneNumber: appData.partnerData.contactNumber,
                partnerType: appData.partnerData.partnerStatus,
            }
            state.partnerData = appData.partnerData;

            if (appData.partnerData.acctType == AccountType.Contractor && appData.partnerData.availablility) {
                state.partnerData.hours = appData.partnerData.availablility;
            }
            console.log('partner data', state.partnerData);
            state.partnerData = appData.partnerData
            state.partnerSettings = appData.settings
            state.homePage = appData.homePage
            state.businessEvents = appData.businessEvents ? appData.businessEvents : []
            state.advertisements = (appData.partnerAds && appData.partnerAds.advertisements) || []
            state.tempAdvertisements = state.advertisements
            state.cookieString = appData.cookieString;
            state.token = appData.token;
            state.clients = appData.settings.clientList || [];

            if (appData.partnerData.groupDeals) {
                state.tempGroupDeals = Object.assign([], appData.partnerData.groupDeals)
            } else {
                state.tempGroupDeals = []
            }
        },
        AUTH_FAILURE(state) {
            state.user = null
        },
        AUTH_LOGOUT(state) {
            state.user = null
        },
        AUTH_LOGGED_OUT(state) {
            state.token = '';
            state.partnerData = null
            state.homePage = null
            state.partnerSettings = null;
            state.advertisements = []
            state.tempAdvertisements = []
            state.tempGroupDeals = []
            state.user = null
        },
        REVEAL_SNACKBAR(state, message) {
            state.snackbarText = message
            state.snackbar = true
            setTimeout(() => {
                state.snackbar = false
            }, 4000)
        },
        ADD_GROUP_DEAL(state, deal) {
            console.log(deal)
            state.tempGroupDeals.push(deal)
        },
        DELETE_GROUP_DEAL(state, deal) {
            const index = state.tempGroupDeals.findIndex(s => s.id === deal.id)
            state.tempGroupDeals.splice(index, 1)
        },
        UPDATE_GROUP_DEAL(state, deal) {
            const index = state.tempGroupDeals.findIndex(s => s.id === deal.id)
            Vue.set(state.tempGroupDeals, index, deal)
        },
        SAVE_GROUP_DEALS(state) {
            const locationId = state.partnerData._id
            MongoMixins.methods
                .submitGroupDeals(locationId, state.tempGroupDeals)
                .then(() => {
                    this.$store.dispatch('SHOW_SNACKBAR', 'Group deals submitted.')
                })
                .catch(e => {
                    this.$store.dispatch('SHOW_SNACKBAR', e.message)
                })
        },
        QUICK_PAY(state, charge) {
            state.sessionCharges.push(charge)
            state.paymentDialog = true
        },
        SHOW_PAYMENT_DIALOG(state) {
            state.paymentDialog = true
        },
        HIDE_PAYMENT_DIALOG(state) {
            state.paymentDialog = false
        },
        SUBMIT_PAYMENT(state, paymentInfo) {
            return MongoMixins.methods.submitPayment(paymentInfo).then((payData) => {
                const result = payData.data.data;
                state.sessionCharges = [];
                state.snackbar = true;
                state.snackbarText = payData.data.message;

                console.log(result);
                if(result.link) {
                    setTimeout(() => {
                        state.snackbarText = 'Redirecting You To Payment Processor';
                    }, 1000);
                    setTimeout(() => {
                        window.open(result.link, '_blank');
                    }, 3000);
                }
                if (result.autoApprove) state.partnerData.partnerType = result.type;
                setTimeout(() => {
                    state.snackbar = false;
                    state.snackbarText = '';
                }, 3000);
            });
        },
        UPDATE_ADVERTISEMENTS(state) {
            console.log('update ' + JSON.stringify(state.advertisements))
            //TODO: Delta checking advertisements against what was last returned from the db and submitting changes.
            return MongoMixins.methods
                .submitAdvertisements(state.partnerData._id, state.tempAdvertisements)
                .then(() => {
                    state.advertisements = state.tempAdvertisements
                })
        },
        CREATE_AD_DESIGN(state, ad) {
            state.tempAdvertisements.push(ad)
        },
        DELETE_AD_DESIGN(state, adIndex) {
            console.log('DELETE DESIGN: ' + adIndex)
            state.tempAdvertisements.splice(adIndex, 1)
        },
        UPDATE_AD_DESIGN(state, updateInfo) {
            console.log('UPDATE DESIGN: ' + updateInfo.localId)
            state.tempAdvertisements.splice(updateInfo.index, 1, updateInfo.ad)
        },
        ACTIVATE_AD(state, adIndex) {
            console.log('act: ' + adIndex)
            const ad = state.tempAdvertisements[adIndex]

            state.tempAdvertisements
                .filter(s => s && s.type === ad.type && s.isActive)
                .forEach(advertisement => {
                    advertisement.isActive = false
                })

            state.tempAdvertisements[adIndex].isActive = true
        },
        UPDATE_ACTIVE_CARD(state, cardAd) {
            const currentActiveCardIndex = state.advertisements.indexOf(s => s.type === AdvertisementType.CARD)

            if (currentActiveCardIndex > -1) {
                state.advertisements[currentActiveCardIndex] = cardAd
            } else {
                state.advertisements.push(cardAd)
            }
        },
        async UPDATE_PARTNER_DATA(state, data) {
            state.partnerData.businessAddress = data.address;
            state.partnerData.slogan = data.slogan;
            state.partnerData.contactNumber = data.contactNumber;
            state.partnerData.capacity = data.capacity;

            const result = await MongoMixins.methods.submitPartnerData(state.partnerData);
            console.log('response data ', result.data);
            console.log('response settings: ', result.data.settings)
            state.partnerData = result.data.data.partnerData;
            state.partnerSettings = result.data.data.settings;
        },
        async SUBMIT_PARTNER_DATA(state) {
            const result = await MongoMixins.methods.submitPartnerData(state.partnerData);

            console.log('response data ', result.data);
            console.log('response settings: ', result.data.settings)
            state.partnerData = result.data.partnerData;
            state.partnerSettings = result.data.settings;
        },
        UPDATE_SETTINGS(state, settings) {
            state.partnerSettings = { ...state.partnerSettings, ...settings };
        },
        async SUBMIT_SETTINGS(state) {
            try {
                const result = await Promise.all([
                    MongoMixins.methods.submitSettings(state.partnerSettings),
                    MongoMixins.methods.updateBusinessPage(state.homePage)
                ]);

                console.log('settings ', result[0].data);
                state.homePage = result[1].data;
                state.partnerSettings = result[0].data;
                console.log('home ', state.homePage);
            } catch (e) {
                console.log('settings update error ' + e.message);
            }
        },
        async UPDATE_BUSINESS_HOURS(state, hours) {
            state.partnerData.hours = hours;

            const result = await MongoMixins.methods.submitPartnerData(state.partnerData)

            console.log('update bus result ', result);
            state.partnerData = result.data;
            state.partnerSettings = result.data.settings;
        }
    },
})
